import './SalesforceUserSelector.scss';

import { SalesforceUser } from './SalesforceUser';
import { SonarSelector } from './SonarSelector';
import { Space } from 'antd';
import { useSalesforceUsers } from '@hooks';

import React, { useCallback, useMemo } from 'react';

export interface SalesforceUserSelectorProps {
	allowReadonly?: boolean;
	avatar?: boolean;
	direction?: 'horizontal' | 'vertical';
	disabled?: boolean;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	id?: string;
	isSpreadsheet?: boolean;
	onChange?: (ids: string[]) => void;
	placeholder?: string;
	value?: string | Array<string>;
}

export const SalesforceUserSelector: React.FC<SalesforceUserSelectorProps> = ({
	allowReadonly,
	avatar,
	direction = 'vertical',
	disabled,
	getPopupContainer,
	id,
	isSpreadsheet,
	onChange,
	placeholder,
	value
}) => {
	const users = useSalesforceUsers();

	const userNames = useMemo(() => users?.reduce((names, user) => {
		names[ user.username ] = user.name;

		return names;
	}, {} as Record<string, string>), [ users ]);

	const selectedUsers = useMemo(() => {
		if (value) {
			return Array.isArray(value) ? value : [ value ];
		}

		return [];
	}, [ value ]);

	const placeholderNode = useMemo(
		() => <SalesforceUser
			avatar={ avatar }
			placeholder={ placeholder }
			username='' />,
		[ avatar, placeholder ]
	);

	const options = useMemo(
		() => users
			?.filter((user) => user.username)
			?.map((user) => ({
				label: <SalesforceUser
					avatar={ avatar }
					name={ user.name }
					username={ user.username } />,
				optionLabel: <SalesforceUser
					avatar={ avatar }
					name={ user.name }
					showTooltip
					username={ user.username } />,
				searchValue: user.name,
				value: user.username
			})),
		[ users, avatar ]
	);

	const readOnlyDisplay = useMemo(() =>
		<Space
			direction={ direction }
			size={ 4 }
			wrap>
			{ selectedUsers.map((username) =>
				<SalesforceUser
					key={ username }
					name={ userNames?.[ username ] }
					showTooltip
					username={ username } />) }
		</Space>,
	[ direction, selectedUsers, userNames ]);

	const handleOnChange = useCallback((values: Array<string>) => {
		if (onChange) {
			onChange(values);
		}
	}, [ onChange ]);

	return (
		<SonarSelector
			allowReadonly={ allowReadonly }
			className='salesforce-user-selector'
			disabled={ disabled }
			getPopupContainer={ getPopupContainer }
			id={ id }
			isSpreadsheet={ isSpreadsheet }
			mode='multiple'
			onChange={ handleOnChange }
			options={ options }
			placeholder={ placeholderNode }
			popupClassName='salesforce-user-space-conscious-label'
			readOnlyDisplay={ value?.length ? readOnlyDisplay : null }
			value={ selectedUsers } />
	);
};
